import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { selectDatasourceId, selectDocumentId } from '@/redux/session/selectors';
import { selectOpenSidebar, selectSidebarMeta } from '@/redux/sidebar/selectors';
import { SidebarOptions } from '@/shared/types/sidebar';

import { AddExternalSources } from './AddExternalSources/AddExternalSources';
import { AddFAQ } from './AddFAQ';
import { AddTxtDocument } from './AddTxtDocument';
import { AddWebpages } from './AddWebpages/AddWebpages';
import { CollectionForm } from './CollectionForm';
import { DatasourceDetails } from './DatasourceDetails';
import { EditDocumentForm } from './EditDocumentForm';
import { IngestionReport } from './IngestionReport';
import { RenameFolderForm } from './RenameFolderForm';
import { UploadDocuments } from './UploadDocuments';
import { UploadFAQs } from './UploadFAQs';

// Keep in sync with the conditionals rendered below so the consumer `<Sidebar>`
// `when` guard and the body never drift.
export const RIGHT_PANEL_SIDEBAR_KINDS: SidebarOptions[] = [
  'datasource_details',
  'create_collection',
  'rename_folder',
  'add_faq',
  'upload_faq',
  'add_txt_document',
  'edit_document',
  'upload_documents',
  'add_webpages',
  'ingestion_report',
  'add_external_sources',
];

export const RightPanelContent = () => {
  const openSidebar = useSelector(selectOpenSidebar);
  const { collectionId, datasourceId } = useParams();
  const reduxDatasourceId = useSelector(selectDatasourceId);
  const documentId = useSelector(selectDocumentId);
  const sidebarMode = useSelector(selectSidebarMeta)?.mode;
  // Keyed so the form remounts when the row selection changes: an edit's values must not leak
  // into the create form. URL first, matching how the panels resolve the id.
  const selectedDatasourceId = datasourceId || reduxDatasourceId;

  return (
    <div className="h-full overflow-auto border-l xl:border-l-0">
      {/* General */}
      {openSidebar === 'datasource_details' && <DatasourceDetails />}
      {openSidebar === 'create_collection' && <CollectionForm />}
      {openSidebar === 'rename_folder' && <RenameFolderForm />}

      {/* FAQ */}
      {openSidebar === 'add_faq' && <AddFAQ key={`${datasourceId}-${documentId}`} />}
      {openSidebar === 'upload_faq' && <UploadFAQs />}

      {/* Documents */}
      {openSidebar === 'add_txt_document' && (
        <AddTxtDocument key={`${datasourceId}-${documentId}`} />
      )}
      {openSidebar === 'edit_document' && <EditDocumentForm />}
      {openSidebar === 'upload_documents' && <UploadDocuments />}

      {/* Webpages */}
      {openSidebar === 'add_webpages' && <AddWebpages key={selectedDatasourceId} />}
      {openSidebar === 'ingestion_report' && (
        <IngestionReport collectionId={collectionId} datasourceId={datasourceId} />
      )}

      {/* External Sources */}
      {openSidebar === 'add_external_sources' && (
        <AddExternalSources key={`${selectedDatasourceId}-${sidebarMode ?? ''}`} />
      )}
    </div>
  );
};
